import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { PrimaryButton } from '@bb-ui/react-library/dist/components';
import { Refresh } from '@bb-ui/icons/dist/medium';
import { JobExecution } from '@lct-jobs/core';
import { PageTemplate } from 'components/PageTemplate';
import { useAccumulatedExecutions } from 'hooks/useAccumulatedExecutions';
import { useExecutionSearch } from 'hooks/useExecutionSearch';
import { ExecutionTable } from './ExecutionTable';

export interface ExecutionListViewProps {
  apiPath: string;
  title: string;
  testIdPrefix: string;
  detailPathBuilder?: (execution: JobExecution) => string;
  headerContent?: React.ReactNode;
}

export const ExecutionListView: React.FC<ExecutionListViewProps> = ({
  apiPath,
  title,
  testIdPrefix,
  detailPathBuilder,
  headerContent,
}) => {
  const { t } = useTranslation();
  const { executions, loading, error, nextPageToken, loadMore, refresh } = useAccumulatedExecutions(
    { url: apiPath },
  );
  const search = useExecutionSearch({ executions, nextPageToken, loading, loadMore });

  let emptyMessage = t('jobService.noData');
  if (search.searchPending) {
    emptyMessage = t('jobService.executionSearching');
  } else if (search.isSearching) {
    emptyMessage = t('jobService.executionNoMatch', { searchExpression: search.searchTerm });
  }

  return (
    <PageTemplate
      title={title}
      data-testid={`${testIdPrefix}-list-page`}
      headerControl={
        <PrimaryButton
          startIcon={<Refresh />}
          onClick={refresh}
          data-testid={`${testIdPrefix}-refresh-button`}
        >
          Refresh
        </PrimaryButton>
      }
    >
      {headerContent}
      <ExecutionTable
        executions={search.matches}
        loading={loading}
        error={error}
        nextPageToken={nextPageToken}
        onLoadMore={search.loadMorePage}
        emptyMessage={emptyMessage}
        testIdPrefix={testIdPrefix}
        detailPathBuilder={detailPathBuilder}
        searchValue={search.inputValue}
        onSearchChange={search.onInputChange}
        searchFieldId={`${testIdPrefix}-search`}
        searchNotice={search.searchCapped ? t('jobService.executionSearchPartial') : undefined}
      />
    </PageTemplate>
  );
};

export default ExecutionListView;
